import * as classnames from "classnames";
import { isEqual } from "lodash";
import * as React from "react";

import { WalkThrough, WalkThroughItem, Msg } from "@shared/components";
import {
  ApplicantChallengeModel,
  ApplicantChallengeResultModel,
} from "@shared/models";
import { ChallengeTakenBy, ProgrammingLanguage } from "@shared/services/enums";

import {
  TimeLimitPanel,
  ImportantPointsPanel,
  SolvingMethodPanel,
  ProgrammingLanguagePanel,
  CodingCheckListPanel,
  ExamEnvironmentPanel,
  isShowCLIPreRequirements,
  isShowCodingChallengeConfirm,
  getCurrentProgrammingLanguage,
} from "./partials";

/**
 * Prop interface
 */
export interface StartChallengeWalkThroughProps {
  isOpen: boolean;
  challenge: ApplicantChallengeModel;
  challengeResult: ApplicantChallengeResultModel;
  onStart: (payload: {
    programmingLanguage?: ProgrammingLanguage;
    takenBy?: ChallengeTakenBy;
  }) => void;
  onCancel: () => void;
}

/**
 * State interface
 */
export interface StartChallengeWalkThroughState {
  programmingLanguage?: ProgrammingLanguage;
  takenBy: ChallengeTakenBy;
  currentIndex: number;
  codingCheckValid: boolean;
}

/**
 * Page component
 */
class StartChallengeWalkThrough extends React.Component<
  StartChallengeWalkThroughProps,
  StartChallengeWalkThroughState
> {
  constructor(props: StartChallengeWalkThroughProps) {
    super(props);

    this.state = {
      programmingLanguage: getCurrentProgrammingLanguage(
        props.challenge,
        props.challengeResult,
      ),
      takenBy: ChallengeTakenBy.WebBrowser,
      currentIndex: 0,
      codingCheckValid: false,
    };
  }

  public componentDidUpdate() {
    const { isOpen, challengeResult, challenge } = this.props;
    if (!isOpen) {
      this.setState({
        programmingLanguage: getCurrentProgrammingLanguage(
          challenge,
          challengeResult,
        ),
        takenBy: ChallengeTakenBy.NotSelected,
        currentIndex: 0,
        codingCheckValid: false,
      });
    }
  }

  public shouldComponentUpdate(
    nextProps: StartChallengeWalkThroughProps,
    nextState: StartChallengeWalkThroughState,
  ) {
    return !isEqual(nextProps, this.props) || !isEqual(nextState, this.state);
  }

  public render() {
    const { isOpen, onCancel, onStart, challenge, challengeResult } =
      this.props;

    const rootStyle = classnames("code-start-challenge-walk-through");

    const items: Array<WalkThroughItem> = [];

    items.push({
      title: <Msg id={"start-challenge.title.important"} />,
      component: (
        <div className="code-c-walk-through__item">
          <ImportantPointsPanel />
        </div>
      ),
    });

    items.push({
      title: <Msg id={"start-challenge.title.examEnv"} />,
      component: (
        <div className="code-c-walk-through__item">
          <ExamEnvironmentPanel />
        </div>
      ),
      hasPrevButton: true,
    });

    if (isShowCodingChallengeConfirm(challenge)) {
      items.push({
        title: <Msg id="start-challenge.button.web" />,
        component: (
          <div className="code-c-walk-through__item">
            <CodingCheckListPanel
              takenBy={this.state.takenBy}
              challenge={challenge}
              onChange={(codingCheckValid) =>
                this.setState({ codingCheckValid })
              }
            />
          </div>
        ),
        hasPrevButton: true,
        nextButtonDisabled: !this.state.codingCheckValid,
      });
    }

    if (isShowCLIPreRequirements(challenge)) {
      items.push({
        title: <Msg id={"start-challenge.step2.title"} />,
        component: (
          <div className="code-c-walk-through__item">
            <SolvingMethodPanel />
          </div>
        ),
        hasPrevButton: true,
        prevButtonType: "primary",
        preventGoPrev: true,
        prevButtonLabel: <Msg id={"start-challenge.button.local"} />,
        nextButtonLabel: <Msg id={"start-challenge.button.web"} />,
        onClickPrev: () =>
          this.setState(
            {
              takenBy: ChallengeTakenBy.LocalMachine,
            },
            () => this.onMovePage(3),
          ),
        onClickNext: () =>
          this.setState({
            takenBy: ChallengeTakenBy.WebBrowser,
          }),
      });

      items.push({
        title: (
          <Msg
            id={
              this.state.takenBy === ChallengeTakenBy.LocalMachine
                ? "start-challenge.button.local"
                : "start-challenge.button.web"
            }
          />
        ),
        component: (
          <div className="code-c-walk-through__item">
            <CodingCheckListPanel
              takenBy={this.state.takenBy}
              challenge={challenge}
              onChange={(codingCheckValid) =>
                this.setState({ codingCheckValid })
              }
            />
          </div>
        ),
        hasPrevButton: true,
        nextButtonDisabled: !this.state.codingCheckValid,
      });
    }

    if (challenge.isProgrammingChallenge()) {
      if (!challengeResult || !challengeResult.programmingLanguage) {
        items.push({
          title: <Msg id={"start-challenge.step3.title"} />,
          component: (
            <div className="code-c-walk-through__item">
              <ProgrammingLanguagePanel
                initialValue={this.state.programmingLanguage}
                challenge={challenge}
                onChange={(programmingLanguage) =>
                  this.setState({ programmingLanguage })
                }
              />
            </div>
          ),
          hasPrevButton: true,
        });
      }
    }

    items.push({
      title: <Msg id={"start-challenge.step4.title"} />,
      component: (
        <div className="code-c-walk-through__item">
          <TimeLimitPanel challenge={challenge} />
        </div>
      ),
      hasPrevButton: true,
      prevButtonLabel: <Msg id={"button.challengeCancelStart"} />,
      nextButtonLabel: <Msg id={"button.challengeStart"} />,
      onClickNext: () =>
        onStart({
          programmingLanguage: this.state.programmingLanguage,
          takenBy: this.state.takenBy,
        }),
      onClickPrev: () => onCancel(),
    });

    return (
      <WalkThrough
        isOpen={isOpen}
        className={rootStyle}
        items={items}
        hasPrevButton={false}
        currentIndex={this.state.currentIndex}
        showSkipButton={false}
        onClose={onCancel}
        onMovePage={this.onMovePage}
      />
    );
  }

  private onMovePage = (currentIndex: number) => {
    this.setState({ currentIndex });
  };
}

export default StartChallengeWalkThrough;
